import Swal, { type SweetAlertIcon } from 'sweetalert2';

/**
 * Configuración base para notificaciones SweetAlert2
 * Aplica DRY principle - elimina duplicación de configuración
 * Cumple con OCP - extensible para nuevos tipos de notificación
 */
export interface NotificationConfig {
  icon: SweetAlertIcon;
  title: string;
  showConfirmButton?: boolean;
  confirmButtonText?: string;
  timer?: number;
  allowOutsideClick?: boolean;
  allowEscapeKey?: boolean;
}

/**
 * Servicio base para notificaciones usando SweetAlert2
 * Cumple con OCP: extensible para nuevos tipos de notificación
 * Aplica DRY: centraliza configuración común eliminando duplicación
 */
export abstract class BaseNotificationService {
  /**
   * Muestra una notificación con configuración personalizada
   * @param message - Mensaje a mostrar al usuario
   * @param config - Configuración visual y comportamiento de la notificación
   */
  protected async showNotification(
    message: string,
    config: NotificationConfig,
  ): Promise<void> {
    await Swal.fire({
      icon: config.icon,
      title: config.title,
      text: message,
      showConfirmButton: config.showConfirmButton ?? true,
      confirmButtonText: config.confirmButtonText,
      timer: config.timer,
      allowOutsideClick: config.allowOutsideClick ?? true,
      allowEscapeKey: config.allowEscapeKey ?? true,
      heightAuto: false,
      backdrop: true,
    });
  }

  /**
   * Configuraciones predefinidas para cada tipo de notificación
   */
  protected static readonly CONFIGS = {
    ERROR: {
      icon: 'error' as SweetAlertIcon,
      title: 'Error',
      showConfirmButton: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
    },
    SUCCESS: {
      icon: 'success' as SweetAlertIcon,
      title: 'Éxito',
      showConfirmButton: false,
      timer: 1200,
      allowOutsideClick: false,
      allowEscapeKey: false,
    },
    SUCCESS_CONFIRM: {
      icon: 'success' as SweetAlertIcon,
      title: 'Éxito',
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      allowOutsideClick: true,
      allowEscapeKey: true,
    },
    WARNING: {
      icon: 'warning' as SweetAlertIcon,
      title: 'Cerrando sesión',
      showConfirmButton: false,
      timer: 1200,
      allowOutsideClick: false,
      allowEscapeKey: false,
    },
    WARNING_INFO: {
      icon: 'warning' as SweetAlertIcon,
      title: 'Advertencia',
      showConfirmButton: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
    },
    INFO: {
      icon: 'info' as SweetAlertIcon,
      title: 'Procesando',
      showConfirmButton: false,
      timer: 1200,
      allowOutsideClick: false,
      allowEscapeKey: false,
    },
  } as const;
}
